import { NgClass } from '@angular/common'
import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core'
import { ApiToken, ApiTokenScopeLevel } from '@app/v2/settings/enums/api-tokens.model'
import { DropdownComponent } from '@app/v2/shared/components/dropdown/dropdown.component'
import { DropdownOption } from '@app/v2/shared/components/dropdown/types/dropdown-option.type'
import { TranslateModule, TranslateService } from '@ngx-translate/core'

export type ApiKeyEventOption = {
  id: number
  name: string
  slug: string
}

type ApiKeyScopeBadge = {
  resourceKey: string
  levelLabel: string
}

const ROTATE_MENU_ICON =
  `<svg width="14" height="14" viewBox="0 0 11 12" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M10.3889 5.4C10.0509 5.4 9.77778 5.6688 9.77778 6C9.77778 8.316 7.85828 10.2 5.5 10.2C4.37067 10.2 3.30183 9.756 2.51228 9H4.27778C4.61572 9 4.88889 8.7312 4.88889 8.4C4.88889 8.0688 4.61572 7.8 4.27778 7.8H1.37683C1.25583 7.7754 1.12933 7.7898 1.00894 7.842C0.733944 8.0022 0.611111 8.1834 0.611111 8.4V11.4C0.611111 11.7312 0.884278 12 1.22222 12C1.56017 12 1.83333 11.7312 1.83333 11.4V10.008C2.82944 10.8846 4.12806 11.4 5.5 11.4C8.53294 11.4 11 8.9772 11 6C11 5.6688 10.7268 5.4 10.3889 5.4Z" fill="#1C64F2"/><path d="M1.22222 6C1.22222 3.684 3.14172 1.8 5.5 1.8C6.62933 1.8 7.69817 2.244 8.48772 3H6.72222C6.38428 3 6.11111 3.2688 6.11111 3.6C6.11111 3.9312 6.38428 4.2 6.72222 4.2H9.62194C9.74356 4.2252 9.87128 4.2102 9.99167 4.1574C10.2661 3.9978 10.3889 3.8166 10.3889 3.6V0.6C10.3889 0.2688 10.1157 0 9.77778 0C9.43983 0 9.16667 0.2688 9.16667 0.6V1.992C8.17056 1.1154 6.87194 0.6 5.5 0.6C2.46706 0.6 0 3.0228 0 6C0 6.3312 0.273167 6.6 0.611111 6.6C0.949056 6.6 1.22222 6.3312 1.22222 6Z" fill="#1C64F2"/></svg>` as const

const EDIT_MENU_ICON =
  `<svg width="14" height="14" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M9.33872 0.465904L8.38084 1.47763L10.586 3.80671L11.5432 2.79499C11.688 2.64207 11.8029 2.46052 11.8813 2.26071C11.9597 2.06089 12 1.84673 12 1.63045C12 1.41416 11.9597 1.2 11.8813 1.00018C11.8029 0.800369 11.688 0.61882 11.5432 0.465904C11.2461 0.166831 10.8514 0 10.441 0C10.0305 0 9.63584 0.166831 9.33872 0.465904Z" fill="#1C64F2"/><path d="M9.70416 4.73808L7.49904 2.409L4.55243 5.51862C4.46525 5.61052 4.4058 5.72764 4.38156 5.8552L4.05354 7.60201C4.03335 7.70838 4.03836 7.81836 4.0681 7.92219C4.09784 8.02602 4.15141 8.12049 4.22403 8.19719C4.29666 8.2739 4.38609 8.33047 4.4844 8.36189C4.5827 8.3933 4.68683 8.39859 4.78754 8.37727L6.44138 8.02817C6.56192 8.00245 6.67258 7.93966 6.75942 7.8477L9.70416 4.73808Z" fill="#1C64F2"/><path d="M7.35436 8.61704C7.18011 8.80076 6.9584 8.92609 6.71702 8.97733L4.51189 9.44302C4.31059 9.48548 4.10248 9.47482 3.90602 9.41196C3.70956 9.3491 3.53082 9.236 3.38566 9.08269C3.24051 8.92938 3.13343 8.74059 3.07392 8.53309C3.01441 8.32558 3.00431 8.10577 3.04452 7.89315L3.48542 5.56407C3.53393 5.30911 3.65259 5.07494 3.82654 4.8909L6.89475 1.65021C6.87768 1.5888 6.86887 1.52517 6.86856 1.46117H1.35139C0.993077 1.46152 0.649539 1.61201 0.396176 1.87962C0.142814 2.14722 0.000330238 2.51007 0 2.88852V10.5726C0.000330238 10.9511 0.142814 11.3139 0.396176 11.5816C0.649539 11.8492 0.993077 11.9997 1.35139 12H8.62654C8.98485 11.9997 9.32839 11.8492 9.58175 11.5816C9.83512 11.3139 9.9776 10.9511 9.97793 10.5726V5.84598L7.35436 8.61704Z" fill="#1C64F2"/></svg>` as const

const REMOVE_MENU_ICON =
  `<svg class="tw-fill-red-600" width="14" height="14" viewBox="0 0 11 12" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M10.3889 2.52632H7.94444V1.26316C7.94444 0.928148 7.81567 0.606858 7.58646 0.36997C7.35725 0.133082 7.04638 0 6.72222 0H4.27778C3.95362 0 3.64275 0.133082 3.41354 0.36997C3.18433 0.606858 3.05556 0.928148 3.05556 1.26316V2.52632H0.611111C0.449034 2.52632 0.293596 2.59286 0.17899 2.7113C0.0643847 2.82975 0 2.99039 0 3.15789C0 3.3254 0.0643847 3.48604 0.17899 3.60449C0.293596 3.72293 0.449034 3.78947 0.611111 3.78947H1.22222V10.7368C1.22222 11.0719 1.35099 11.3931 1.5802 11.63C1.80941 11.8669 2.12029 12 2.44444 12H8.55556C8.87971 12 9.19059 11.8669 9.4198 11.63C9.64901 11.3931 9.77778 11.0719 9.77778 10.7368V3.78947H10.3889C10.551 3.78947 10.7064 3.72293 10.821 3.60449C10.9356 3.48604 11 3.3254 11 3.15789C11 2.99039 10.9356 2.82975 10.821 2.7113C10.7064 2.59286 10.551 2.52632 10.3889 2.52632ZM4.27778 1.26316H6.72222V2.52632H4.27778V1.26316ZM4.88889 9.47368C4.88889 9.64119 4.8245 9.80183 4.7099 9.92028C4.59529 10.0387 4.43985 10.1053 4.27778 10.1053C4.1157 10.1053 3.96026 10.0387 3.84566 9.92028C3.73105 9.80183 3.66667 9.64119 3.66667 9.47368V5.05263C3.66667 4.88513 3.73105 4.72448 3.84566 4.60604C3.96026 4.48759 4.1157 4.42105 4.27778 4.42105C4.43985 4.42105 4.59529 4.48759 4.7099 4.60604C4.8245 4.72448 4.88889 4.88513 4.88889 5.05263V9.47368ZM7.33333 9.47368C7.33333 9.64119 7.26895 9.80183 7.15434 9.92028C7.03974 10.0387 6.8843 10.1053 6.72222 10.1053C6.56015 10.1053 6.40471 10.0387 6.2901 9.92028C6.1755 9.80183 6.11111 9.64119 6.11111 9.47368V5.05263C6.11111 4.88513 6.1755 4.72448 6.2901 4.60604C6.40471 4.48759 6.56015 4.42105 6.72222 4.42105C6.8843 4.42105 7.03974 4.48759 7.15434 4.60604C7.26895 4.72448 7.33333 4.88513 7.33333 5.05263V9.47368Z" /></svg>` as const

@Component({
  selector: 'api-key-card',
  standalone: true,
  imports: [NgClass, TranslateModule, DropdownComponent],
  templateUrl: './api-key-card.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ApiKeyCardComponent {
  public readonly apiToken = input.required<ApiToken>()
  public readonly events = input.required<ApiKeyEventOption[]>()
  public readonly workspaceName = input('')
  public readonly editRequested = output<ApiToken>()
  public readonly deleteRequested = output<ApiToken>()
  public readonly rotateRequested = output<ApiToken>()

  public readonly isOrgWide = computed(() => this.apiToken().event_id == null)
  public readonly isExpired = computed(() => this._isExpired(this.apiToken().expires_at))
  public readonly eventName = computed(() => this.events().find((event) => event.id === this.apiToken().event_id)?.name ?? '')
  public readonly scopeBadges = computed(() => this._buildScopeBadges(this.apiToken().scopes))
  public readonly actionMenuOptions = computed<DropdownOption[]>(() => [
    {
      key: 'rotate',
      value: this._translate.instant('v2.orgSettings.apiKeys.rotateApiKey'),
      prefixIcon: ROTATE_MENU_ICON
    },
    {
      key: 'edit',
      value: this._translate.instant('v2.orgSettings.apiKeys.editApiKey'),
      prefixIcon: EDIT_MENU_ICON
    },
    {
      key: 'remove',
      value: this._translate.instant('v2.orgSettings.apiKeys.removeApiKey'),
      prefixIcon: REMOVE_MENU_ICON
    }
  ])

  private readonly _translate = inject(TranslateService)

  public onEdit(): void {
    this.editRequested.emit(this.apiToken())
  }

  public onDelete(): void {
    this.deleteRequested.emit(this.apiToken())
  }

  public onRotate(): void {
    this.rotateRequested.emit(this.apiToken())
  }

  public onMenuOption(option: DropdownOption): void {
    if (option.key === 'edit') this.onEdit()
    else if (option.key === 'remove') this.onDelete()
    else if (option.key === 'rotate') this.onRotate()
  }

  public formatApiDate(value?: string): string {
    if (!value) return ''

    const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/)
    if (!match) return ''

    const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])))
    const locale = this._translate.currentLang === 'ar' ? 'ar' : 'en-GB'

    return new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' }).format(date)
  }

  /**
   * Whether the API token expiry datetime has already passed.
   * @param value {string | undefined} - Token `expires_at` from the API.
   * @returns {boolean}
   */
  private _isExpired(value?: string): boolean {
    if (!value) return false

    const match = value.match(/^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2}):(\d{2}))?/)
    if (!match) {
      const parsed = new Date(value)
      return !Number.isNaN(parsed.getTime()) && Date.now() > parsed.getTime()
    }

    const expiresAtUtc = Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]), Number(match[4] ?? 23), Number(match[5] ?? 59), Number(match[6] ?? 59))

    return Date.now() > expiresAtUtc
  }

  private _buildScopeBadges(scopes: ApiToken['scopes']): ApiKeyScopeBadge[] {
    const badges: ApiKeyScopeBadge[] = []
    const registrationLevel = this._levelLabel(scopes?.registrations)
    const eventLevel = this._levelLabel(scopes?.event)

    if (registrationLevel) badges.push({ resourceKey: 'v2.orgSettings.apiKeys.scopeRegistration', levelLabel: registrationLevel })
    if (eventLevel) badges.push({ resourceKey: 'v2.orgSettings.apiKeys.scopeEventData', levelLabel: eventLevel })

    return badges
  }

  private _levelLabel(levels?: ApiTokenScopeLevel[]): string | null {
    if (!levels?.length) return null

    const permissionLabels: Record<ApiTokenScopeLevel, string> = {
      read: this._translate.instant('v2.orgSettings.apiKeys.scopePermissionRead'),
      write: this._translate.instant('v2.orgSettings.apiKeys.scopePermissionWrite'),
      delete: this._translate.instant('v2.orgSettings.apiKeys.scopePermissionDelete')
    }

    const order: ApiTokenScopeLevel[] = ['read', 'write', 'delete']
    const labels = order.filter((level) => levels.includes(level)).map((level) => permissionLabels[level])

    return labels.length ? `“${labels.join(', ')}”` : null
  }
}
